/**
 * useJobNotifications Hook
 * React hook for managing async job notifications
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import jobNotificationService from '../services/jobNotificationService';

export function useJobNotifications(token) {
  const [activeJobs, setActiveJobs] = useState([]);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const tokenRef = useRef(token);

  // Keep token ref updated
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Request notification permission on mount
  useEffect(() => {
    const requestPermission = async () => {
      const granted = await jobNotificationService.requestPermission();
      setPermissionGranted(granted);
      if (granted) {
        toast.success('Notifications enabled! You\'ll be notified when processing completes.');
      }
    };
    requestPermission();

    // Cleanup on unmount
    return () => {
      jobNotificationService.stopAllPolling();
    };
  }, []);

  /**
   * Track a new job
   */
  const trackJob = useCallback((jobId, filename) => {
    if (!tokenRef.current) return;

    // Add to active jobs
    setActiveJobs(prev => {
      if (prev.find(j => j.jobId === jobId)) return prev;
      return [...prev, { jobId, filename, progress: 0, status: 'QUEUED' }];
    });

    // Show initial toast
    toast.loading(`Processing ${filename}...`, {
      id: `job-${jobId}`,
      duration: Infinity
    });

    // Start polling
    jobNotificationService.startPolling(jobId, tokenRef.current, {
      onProgress: (data) => {
        setActiveJobs(prev => prev.map(j => 
          j.jobId === jobId 
            ? { ...j, progress: data.progress, status: data.status }
            : j
        ));

        // Update toast with progress
        if (data.progress > 0 && data.progress < 100) {
          toast.loading(`Processing ${filename}... ${Math.round(data.progress)}%`, {
            id: `job-${jobId}`
          });
        }
      },
      onComplete: (data) => {
        setActiveJobs(prev => prev.filter(j => j.jobId !== jobId));
        toast.success(`${filename} processed successfully!`, {
          id: `job-${jobId}`,
          duration: 5000
        });
      },
      onError: (data) => {
        setActiveJobs(prev => prev.filter(j => j.jobId !== jobId));
        toast.error(`Failed to process ${filename}: ${data.error || 'Unknown error'}`, {
          id: `job-${jobId}`,
          duration: 5000
        });
      }
    });
  }, []);

  /**
   * Cancel tracking a job
   */
  const cancelTracking = useCallback((jobId) => {
    jobNotificationService.stopPolling(jobId);
    setActiveJobs(prev => prev.filter(j => j.jobId !== jobId));
    toast.dismiss(`job-${jobId}`);
  }, []);

  /**
   * Request permission manually
   */
  const requestPermission = useCallback(async () => {
    const granted = await jobNotificationService.requestPermission();
    setPermissionGranted(granted);
    return granted;
  }, []);

  return {
    activeJobs,
    activeJobCount: activeJobs.length,
    permissionGranted,
    trackJob,
    cancelTracking,
    requestPermission
  };
}

export default useJobNotifications;
